/** Calls to our API: same origin, session cookie, `X-Jabal: 1` on changes (the server refuses changes without it). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown,
  ) {
    super(code);
  }
}

/** `keepalive`: the request finishes even if the page goes to the background (opening WhatsApp). */
async function call<T>(method: string, path: string, body?: unknown, opts: { keepalive?: boolean } = {}): Promise<T> {
  const isForm = body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      keepalive: opts.keepalive,
      headers: {
        Accept: 'application/json',
        ...(method === 'GET' ? {} : { 'X-Jabal': '1' }),
        ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'OFFLINE', null); // no network
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // not our API answering (the dev proxy or a gateway): the server isn't running / reachable
    if (!res.ok) throw new ApiError(res.status, 'SERVER_DOWN', text);
  }
  if (!res.ok) {
    const code =
      data && typeof data === 'object' && 'code' in data
        ? String(data.code)
        : data === null && res.status >= 500
          ? 'SERVER_DOWN'
          : `HTTP_${res.status}`;
    throw new ApiError(res.status, code, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown, opts?: { keepalive?: boolean }) => call<T>('POST', path, body ?? {}, opts),
  put: <T>(path: string, body: unknown) => call<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => call<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => call<T>('DELETE', path, body),
};
